// Import Package
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import RichTextEditor from "react-rte";
import { useSetRecoilState } from 'recoil';

// Import Material UI
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Close from "@material-ui/icons/Close";
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { CircularProgress, DialogActions } from "@material-ui/core";
import Autocomplete from '@material-ui/lab/Autocomplete';

// Import Icons
import lockedIcon from "../../../../../../../assets/icons/moduleIcons/hash-value-icon.svg";
import unlockedIcon from "../../../../../../../assets/icons/moduleIcons/show-value-icon.svg";

// Import Pages

import DictionaryVariableEditor
    from "../../../../../../../components/DictionaryVariableEditor/DictionaryVariableEditor";
import ArrayVariableEditor from "../../../../../../../components/ArrayVariableEditor/ArrayVariableEditor";
import DataTableVariableEditor from "../../../../../../../components/DataTableVariableEditor/DataTableVariableEditor";
import { alertState } from "../../../../../../../recoil-store/recoil-state";
import { RadioOption, RadioPicker } from "../../../../../../../components/FlowConfigurationInputs/RadioPicker/RadioPicker";

// Import Services
import VariableService from "../../../../../../../services/VariableService/VariableService";
import ApplicationService from "../../../../../../../services/ApplicationService/ApplicationService";
import MateService from "../../../../../../../services/MateService/MateService";

// Import Helpers
import { checkSpecialCharacter } from "../../../../../../../helpers/helper";
import { timeZones } from "../timeZones/TimeZones";

// Import Css
import './AddNewVariables.css';

const useStyles = makeStyles((theme) => ({
    formControl: {
        minWidth: 120,
    },
    selectEmpty: {
        marginTop: theme.spacing(2),
    },
    option: {
        '& > span': {
            marginRight: 10,
            fontSize: 12,
        },
        fontSize: 13,
        fontWeight: 600,
        color: '#46474E',
    },
    inputRoot: {
        "& fieldset": {
            borderColor: '#E6E6E6',
            borderWidth: '1.1px'
        }
    },
    popupIndicator: {
        color: "#B0B0B0",
        height: "10px",
        width: "10px"
    }
}));

function AddNewVariables(props) {
    const { setOpen, variableType, setVariables, automVersionId, mateId } = props;
    const params = useParams();
    const { t } = useTranslation();
    const [variableName, setVariableName] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [hiddenTitle, setHiddenTitle] = useState(true);
    const [variableDesc, setVariableDesc] = useState(RichTextEditor.createEmptyValue());
    const [type, setType] = useState("");
    const [variableAs, setVariableAs] = useState('');
    const [applications, setApplications] = useState([]);
    const [btnDisabled, setBtnDisabled] = useState(true);
    const [defaultValue, setDefaultValue] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [selectedTimeZone, setSelectedTimeZone] = useState({});
    const [loading, setLoading] = useState(false);
    const setAlert = useSetRecoilState(alertState);
    const ref = useRef(null);
    const appConnectorTypeId = '8334e115-cbb3-4254-9453-b9084aed4f67';
    const classes = useStyles();

    useEffect(() => {
        // fetchCredentials()
        getApplications().then(r => r);
        getDefaultTimeZone().then(r => r);
        // fetchAttributes()
    }, []);

    const getApplications = async () => {
        const response = await ApplicationService.getApplications();
        if (response?.status) {
            setApplications(response.data);
        }
    };

    const getDefaultTimeZone = async () => {
        const response = await MateService.getMateByID(mateId);
        if (response.code === 200) {
            let z = timeZones.find(x => x.value === response.data[0].time_zone);
            if (z) {
                setSelectedTimeZone(z);
            }
        }
    }

    const handleClose = () => {
        setOpen(false);
    };

    //set variable Type
    function handleChangeType(e) {
        let filterData = variableType.filter(item => item.id === e.target.value)
        if (filterData[0]?.name === "boolean") {
            setDefaultValue(true);
        } else if (filterData[0]?.name === "dictionary" || filterData[0]?.name === "array") {
            setDefaultValue([]);
        } else if (filterData[0]?.name === "data table") {
            setDefaultValue([
                ['', ''], ['', '']
            ]);
        } else if (filterData[0]?.name === "number") {
            setDefaultValue(0);
        } else if (filterData[0]?.name === "object") {
            setDefaultValue('{}');
        } else if (filterData[0]?.name === "app connector") {
            setDefaultValue('');
            setVariableAs('input');
        } else if (filterData[0]?.name === "string") {
            setHiddenTitle(false)
            setDefaultValue('');
        } else if (filterData[0]?.name === "date") {
            setDefaultValue("1970-01-01T11:00")
        }
        setType(e.target.value)
        if (variableName.length > 0) setBtnDisabled(false);
        else setBtnDisabled(true);
        if (filterData[0]?.name !== "string") setHiddenTitle(true)

    }

    //set variableName changed
    function handleChange(e) {
        let name = e.target.value;
        setVariableName(name);
        if (type.length > 0 && name.length > 0) setBtnDisabled(false);
        else setBtnDisabled(true);
    }

    // set display name changed
    function changeDisplayName(e) {
        if (e.target.value !== ' ') {
            setDisplayName(e.target.value);
        }
    }
    const handleClickShowPassword = (event) => {
        event.preventDefault();
        setShowPassword((pre) => !pre)
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const handleSubmit = async () => {
        // there is a one flow a one variable name so filtered variables
        const getByVariable = props.variables && props.variables.filter(variable => variable.name.toLowerCase() === variableName.toLowerCase());
        if (getByVariable?.length === 0) {
            if (variableName.length < 3) {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t("MESSAGES.AUTOMS.VARIABLE-NAME-CHARACTER-LENGTH-ERROR"),
                    severity: 'error'
                }));
            } else if (["TRIGGERPARAM", "SYS_ACTION_FIELDS", "TRIGGERHEADER"].includes(variableName.toUpperCase())) {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t("MESSAGES.AUTOMS.SYSTEM-VARIABLE-CANNOT-BE-ADDED"),
                    severity: 'error'
                }));
            } else if (!checkSpecialCharacter(variableName)) {
                let newData = {
                    name: variableName,
                    display_name: displayName,
                    type_id: type,
                    description: variableDesc.toString('html'),
                    is_input: variableAs === 'input',
                    is_output: variableAs === 'output',
                    is_customizable: variableAs !== '',
                    autom_version_id: automVersionId,
                    default_value: '',
                    is_masked: false,
                    time_zone: selectedTimeZone?.value ? selectedTimeZone.value : null
                    // connection_id: null,
                    // attribute_id: null
                }
                const filterData = variableType.filter(item => item.id === type)
                switch (filterData[0]?.name) {
                    case 'string':
                        newData.is_masked = showPassword
                        newData.default_value = defaultValue;
                        break;
                    case 'date':
                    case 'app connector':
                        newData.default_value = defaultValue;
                        break;
                    case 'number':
                    case 'boolean':
                        newData.default_value = defaultValue.toString();
                        break
                    case 'data table':
                        if (Array.isArray(defaultValue)) {
                            newData.default_value = JSON.stringify(defaultValue);
                        } else if (defaultValue.file && defaultValue.file.name) {
                            // upload file

                            let form_data = new FormData();
                            for (const key in newData) {
                                form_data.append(key, newData[key]);
                            }
                            form_data.append("default_file", defaultValue.file);
                            form_data.append("sheet_name", defaultValue.sheetName ? defaultValue.sheetName : '');
                            newData = form_data;
                        } else {
                            newData.default_value = JSON.stringify([['', ''], ['', '']]);
                        }
                        break;
                    case 'object':
                        if (defaultValue !== '{}') {
                            try {
                                JSON.parse(defaultValue);
                            } catch (error) {
                                setAlert(alert => ({
                                    ...alert,
                                    show: true,
                                    message: t("MESSAGES.AUTOMS.OBJECT-TYPE-IS-NOT-VALID"),
                                    severity: 'error'
                                }));
                                return;
                            }
                        }
                        newData.default_value = defaultValue;
                        break;
                    default:
                        newData.default_value = JSON.stringify(defaultValue);
                        break;
                }
                ref.current.style.pointerEvents = "none";
                if (filterData[0]?.name === "data table" && defaultValue.file && defaultValue.file.name) {
                    setLoading(true)
                }
                const response = await VariableService.postVariable(newData);

                if (response.status) {
                    localStorage.setItem("variablename", variableName);
                    const typeName = variableType.filter(i => i.id === type);
                    newData.type = typeName[0].name;

                    async function getVariables() {
                        VariableService.getVariable(automVersionId).then((resp) => {
                            setVariables(resp.data);
                        });
                    }

                    await getVariables();
                    ref.current.style.pointerEvents = "auto";
                    setAlert(alert => ({
                        ...alert,
                        show: true,
                        message: t("MESSAGES.AUTOMS.VARIABLE-" + response.message),
                        severity: 'success'
                    }));
                    setOpen(false);
                } else {
                    setAlert(alert => ({
                        ...alert,
                        show: true,
                        message: t("MESSAGES.AUTOMS.VARIABLE-" + response.message),
                        severity: 'error'
                    }));
                    ref.current.style.pointerEvents = "auto";
                }
                if (filterData[0]?.name === "data table" && defaultValue.file && defaultValue.file.name) {
                    setLoading(false)
                }
            } else {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t("MESSAGES.AUTOMS.VARIABLES-SPEACIAL-CHARACTERS"),
                    severity: 'error'
                }));
            }
        } else {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t("MESSAGES.AUTOMS.SAME-NAME-VARIABLE"),
                severity: 'error'
            }));
        }


    };

    function defaultValueComponent() {
        let filterData = variableType.filter(item => item.id === type)
        if (filterData[0]?.name === "string") {
            return (
                <>
                    <DialogContentText className="string-default-value-container">
                        <span className="dialog-text">{t("CONTENT.AUTOMS-LIST.DEFAULT-VALUE")}</span>
                        <TextField
                            type={!showPassword ? "text" : "password"}
                            // disabled={tab !== 'defaultValue'}
                            size="small"
                            style={{ width: "100%" }}
                            id="outlined-basic"
                            variant="outlined"
                            value={defaultValue}
                            onChange={(e) => setDefaultValue(e.target.value)}
                            inputProps={{ style: { fontSize: 12 } }}
                        />
                        <img className="lock-icon" src={showPassword ? lockedIcon : unlockedIcon} onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword} />

                    </DialogContentText>
                </>
            );
        } else if (filterData[0]?.name === "number") {
            return (
                <TextField
                    size="small"
                    type="number"
                    style={{ width: "100%" }}
                    id="outlined-basic"
                    variant="outlined"
                    value={defaultValue}
                    onChange={(e) => {
                        if (e.target.value === '') {
                            e.target.value = 0;
                        }
                        setDefaultValue(e.target.value)
                    }}
                    inputProps={{ style: { fontSize: 12 } }}
                />
            );
        } else if (filterData[0]?.name === "boolean") {
            return (<div className="mail-server-button-group">
                <div
                    onClick={() => setDefaultValue(true)}
                    className={
                        defaultValue === true
                            ? "mail-server-button-active"
                            : "mail-server-button"
                    }
                >
                    {t("CONTENT.AUTOMS-LIST.TRUE")}
                </div>
                <div
                    onClick={() => setDefaultValue(false)}
                    className={
                        defaultValue === false
                            ? "mail-server-button-active"
                            : "mail-server-button"
                    }
                >
                    {t("CONTENT.AUTOMS-LIST.FALSE")}
                </div>
            </div>)
        } else if (filterData[0]?.name === "dictionary") {
            return (<DictionaryVariableEditor
                setData={setDefaultValue}
                data={defaultValue}
                titles={["KEY", "VALUE"]}
                header="DICTIONARY-VARIABLE-EDITOR"
            />)
        } else if (filterData[0]?.name === "array") {
            return (<ArrayVariableEditor
                setData={setDefaultValue}
                data={defaultValue}
                titles={["VALUE"]}
                header="DICTIONARY-VARIABLE-EDITOR"
            />)
        } else if (filterData[0]?.name === "data table") {
            return (<DataTableVariableEditor
                setData={setDefaultValue}
                data={defaultValue}
                from="addVariable"
            />)
        } else if (filterData[0]?.name === "date") {
            return (
                <TextField type="datetime-local" value={defaultValue}
                    onChange={e => setDefaultValue(e.target.value)}
                />
            );
        } else if (filterData[0]?.name === 'object') {
            return (
                <TextField
                    multiline rows={4}
                    size="small"
                    style={{ width: "100%" }}
                    id="outlined-basic"
                    variant="outlined"
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value)}
                    inputProps={{ style: { fontSize: 12 } }}
                />
            )
        } else if (filterData[0]?.name === 'app connector') {
            return (
                <FormControl
                    variant="outlined"
                    className={classes.formControl}
                    style={{ width: "100%" }}
                >
                    <Select
                        labelId="demo-simple-select-outlined-label"
                        style={{
                            backgroundColor: "#fff",
                            height: "40px",
                            fontSize: "12px",
                        }}
                        id="demo-simple-select-outlined"
                        value={defaultValue}
                        onChange={(e) => setDefaultValue(e.target.value)}
                    >
                        {applications.length > 0 &&
                            applications.map((app) => (
                                <MenuItem
                                    key={app.id}
                                    style={{ fontSize: 12 }}
                                    value={app.id}
                                >
                                    {app.name}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
            )
        } else {

        }
    }

    const variableAsChange = (event) => {
        setVariableAs(event.target.value);
    }

    return (
        <Dialog
            open={true}
            onClose={handleClose}
            data-test="about-dialog"
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="variables-dialog"
        >
            <DialogTitle
                id="alert-dialog-title">
                <div id="about-title">
                    {t("CONTENT.AUTOMS-LIST.ADD-NEW-VARIABLES")}
                    <Button data-test="cancelautom-button" onClick={handleClose}>
                        <Close />
                    </Button>
                </div>
            </DialogTitle>
            <DialogContent id="alert-dialog-description" className="dialog-content-area">
                <span className="dialog-text">{t("CONTENT.AUTOMS-LIST.VARIABLES-TYPE")}</span>
                <DialogContentText>
                    <FormControl
                        className={classes.formControl}
                        style={{ width: "100%" }}
                    >
                        <Select
                            style={{ height: "40px", fontSize: "12px" }}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            variant="outlined"
                            placeholder="Select Type"
                            onChange={(e) => handleChangeType(e)}
                        >
                            {variableType !== undefined && variableType && variableType?.length > 0 ? variableType.map((item, index) => item !== undefined &&
                                <MenuItem style={{ fontSize: 12 }} key={item.id} value={item.id}>{item.name}</MenuItem>) :
                                <MenuItem style={{ fontSize: 12 }} />}

                        </Select>
                    </FormControl>
                </DialogContentText>
                <span className="dialog-text">{t("CONTENT.AUTOMS-LIST.VARIABLES-NAME")}</span>
                <DialogContentText>
                    <TextField
                        size="small"
                        style={{ width: "100%" }}
                        id="outlined-basic"
                        variant="outlined"
                        onChange={(e) => {
                            if (e.target && e.target.value && e.target.value.length > 120) {
                                return setAlert(alert => ({
                                    ...alert,
                                    show: true,
                                    message: t('CONTENT.GENERAL.CHARACTER-LIMIT', { limit: 120 }),
                                    severity: 'error'
                                }))
                            }
                            handleChange(e)
                        }}
                        inputProps={{ style: { fontSize: 12 } }}
                    />
                </DialogContentText>
                <span className="dialog-text">{t("CONTENT.AUTOMS-LIST.VARIABLES-DISPLAY-NAME")}</span>
                <DialogContentText>
                    <TextField
                        size="small"
                        style={{ width: "100%" }}
                        id="outlined-basic"
                        variant="outlined"
                        value={displayName}
                        onChange={(e) => {
                            if (e.target && e.target.value && e.target.value.length > 120) {
                                return setAlert(alert => ({
                                    ...alert,
                                    show: true,
                                    message: t('CONTENT.GENERAL.CHARACTER-LIMIT', { limit: 120 }),
                                    severity: 'error'
                                }))
                            }
                            changeDisplayName(e)
                        }}
                        inputProps={{ style: { fontSize: 12 } }}
                    />
                </DialogContentText>
                {
                    type === "f6cd7e10-1b2a-4e43-b1fa-d6b7007988c6" && // date type
                    <>
                        <span className="dialog-text">{t("CONTENT.AUTOMS-LIST.TIME-ZONE")}</span>
                        <Autocomplete
                            value={selectedTimeZone}
                            onChange={(e, newValue) => setSelectedTimeZone(newValue ? newValue : {})}
                            id={`variable-time-zone`}
                            style={{ position: 'relative', background: '#FBFBFB' }}
                            size="small"
                            options={timeZones}
                            classes={{
                                option: classes.option,
                                inputRoot: classes.inputRoot,
                                popupIndicator: classes.popupIndicator
                            }}
                            autoHighlight
                            getOptionLabel={(option) => option.value ? `${option.value} ${option.dst ? `(Standard: ${option.utc}, DST: ${option.dst})` : `(${option.utc})`}` : ""}
                            renderOption={(option) => (
                                <>
                                    {`${option.value} ${option.dst ? `(Standard: ${option.utc}, DST: ${option.dst})` : `(${option.utc})`}`}
                                </>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    id='select-time-zone-text'
                                    className="select-action-input"
                                    placeholder={t("CONTENT.AUTOMS-LIST.SELECT-TIME-ZONE")}
                                    variant="outlined"
                                    classes={{
                                        root: classes.inputRoot
                                    }}
                                    inputProps={{
                                        ...params.inputProps,
                                        "aria-autocomplete": 'new-password',
                                        autoComplete: 'new-password', // disable autocomplete and autofill
                                        style: {
                                            marginLeft: '5px',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: '#46474E'
                                        }
                                    }}
                                />
                            )}
                        />
                        <small style={{ marginBottom: "15px", fontSize: "10px" }} className="conf-field-desc"><em>{t('CONTENT.AUTOMS-LIST.BASED-ON-SYSTEM-TIME-ZONE')}</em></small>
                    </>
                }

                {variableType?.filter(item => item.id === type)[0]?.name === "app connector" ? (
                    <span className="dialog-text">{t("CONTENT.AUTOMS-LIST.APPLICATION")}</span>
                ) : variableType?.filter(item => item.id === type)[0]?.name && hiddenTitle ? (
                    <span className="dialog-text">{t("CONTENT.AUTOMS-LIST.DEFAULT-VALUE")}</span>
                ) : (
                    <span className="dialog-text" />
                )}
                <DialogContentText>
                    {defaultValueComponent()}
                </DialogContentText>
                <span className="dialog-text">{t("CONTENT.AUTOMS-LIST.VARIABLES-DESCRIPTION")}</span>
                <DialogContentText>
                    <RichTextEditor
                        value={variableDesc}
                        editorClassName="publish-editor"
                        onChange={value => setVariableDesc(value)}
                    // toolbarConfig={toolbarConfig}
                    />

                </DialogContentText>

                <span className="dialog-text">{t("CONTENT.AUTOMS-LIST.SET-AS")}</span>
                <div className="dialog-set-options">
                    <RadioGroup style={{ marginBottom: '12px' }} row aria-label="position" name="position"
                        value={variableAs} onChange={variableAsChange}>
                        <FormControlLabel value="input" control={<Radio style={{ color: "#C4C4C4" }} />} label="Input" />

                        <FormControlLabel disabled={type === appConnectorTypeId} value="output" control={<Radio style={{ color: "#C4C4C4" }} />} label="Output" />
                    </RadioGroup>
                </div>
                <DialogContentText>
                    <Button
                        ref={ref}
                        data-test="submitdialog-button"
                        onClick={handleSubmit}
                        variant="outlined"
                        disabled={btnDisabled || (type === appConnectorTypeId && defaultValue === '')}
                        style={{
                            float: "right",
                            backgroundColor: "#46474E",
                            borderRadius: "2px",
                            color: "#fff",
                            width: "40%",
                            border: "none"
                        }}>
                        {t("CONTENT.ROLE.SAVE")}
                    </Button>
                </DialogContentText>
            </DialogContent>

            {loading && <Dialog open={true} maxWidth={'sm'}><DialogActions style={{ padding: '30px' }}><CircularProgress /></DialogActions></Dialog >}

        </Dialog>
    );
}

export default AddNewVariables;
